import type { ItemType } from 'antd/es/menu/interface';
import i18n from 'i18next';
import { groupBy, partition } from 'lodash';

type TTemplateItem = {
  __typename?: 'TaskTemplate' | undefined;
  id: any;
  name: string;
  global?: boolean | null;
  workContainerType: { __typename?: 'WorkContainerType' | undefined; id: any; name: string };
};

type TTemplateGroup = {
  key: string;
  label: string;
  typeName: string;
  templates: TTemplateItem[];
};

// Builds flat groups with a category prefix in the label, e.g. "SkySpecs Standard Templates - Repair Task".
// `categoryKey` is a stable, untranslated identifier used for React keys; `categoryLabel` is the
// translated, user-facing display string used in the group title.
function buildFlatTypeGroups(
  templates: TTemplateItem[],
  categoryKey: string,
  categoryLabel: string
): TTemplateGroup[] {
  const grouped = groupBy(templates, 'workContainerType.name');
  return Object.keys(grouped).map((typeName) => ({
    key: `${categoryKey}_${typeName}`,
    label: i18n.t('taskTemplates.categoryTypeGroup', '{{category}} - {{type}}', {
      category: categoryLabel,
      type: typeName,
    }),
    typeName,
    templates: grouped[typeName],
  }));
}

// Single-level grouping, by work container type AND global/custom status, with custom templates
// after the global ones
function groupTemplates(templatesData: TTemplateItem[]): TTemplateGroup[] {
  const [globalTemplates, customTemplates] = partition(templatesData, ['global', true]);
  return [
    ...buildFlatTypeGroups(
      globalTemplates,
      'SkySpecs Standard Templates',
      i18n.t('taskTemplates.skySpecsStandardTemplates', 'SkySpecs Standard Templates')
    ),
    ...buildFlatTypeGroups(
      customTemplates,
      'Custom Templates',
      i18n.t('taskTemplates.customTemplates', 'Custom Templates')
    ),
  ];
}

export function formatTemplatesMenuItems(templatesData: TTemplateItem[]): ItemType[] {
  return groupTemplates(templatesData).map(({ key, label, typeName, templates }) => ({
    type: 'group',
    key,
    label,
    children: templates.map((template) => ({
      key: `${typeName}_${template.id}`,
      label: template.name,
    })),
  }));
}

export function formatTemplatesSelectOptions(templatesData: TTemplateItem[]) {
  return groupTemplates(templatesData).map(({ label, templates }) => ({
    label,
    options: templates.map((template) => ({ value: template.id, label: template.name })),
  }));
}
